/**
 * core/utils/defaults.ts — Default configurations for the widget grid,
 * tab layouts, and widget config values.
 *
 * All layout presets are defined here so they can be shared between the
 * Settings tab and the Zustand store without circular dependencies.
 */

import type { TabLayoutMap, TabId, WidgetGridItem, AppTab, UserSettings } from '../types';

// ─── Grid Constants ───────────────────────────────────────────────────────────

/** Number of grid columns at each breakpoint (react-grid-layout) */
export const GRID_COLS = {
  xl: 12,
  lg: 12,
  md: 10,
  sm: 6,
  xs: 4,
  xxs: 2,
} as const;

/** Row height in pixels */
export const GRID_ROW_HEIGHT = 60;

/** Margin between widgets [horizontal, vertical] in pixels */
export const GRID_MARGIN: [number, number] = [12, 12];

/** Container padding [horizontal, vertical] in pixels */
export const GRID_CONTAINER_PADDING: [number, number] = [12, 12];

// ─── Layout Presets ──────────────────────────────────────────────────────────

/**
 * The four built-in layout presets for the Home tab.
 * Switching a preset replaces the Home tab layout in the store.
 */

const FOCUSED_HOME: WidgetGridItem[] = [
  { instanceId: 'focus_suggestion', widgetId: 'focus_suggestion', x: 0, y: 0, w: 8, h: 4, config: {} },
  { instanceId: 'quick_actions', widgetId: 'quick_actions', x: 8, y: 0, w: 4, h: 4, config: {} },
  { instanceId: 'my_tasks', widgetId: 'my_tasks', x: 0, y: 4, w: 12, h: 6, config: { maxItems: 10 } },
];

const BALANCED_HOME: WidgetGridItem[] = [
  { instanceId: 'focus_suggestion', widgetId: 'focus_suggestion', x: 0, y: 0, w: 8, h: 3, config: {} },
  { instanceId: 'alerts_home', widgetId: 'alerts', x: 8, y: 0, w: 4, h: 3, config: {} },
  { instanceId: 'my_tasks', widgetId: 'my_tasks', x: 0, y: 3, w: 6, h: 5, config: { maxItems: 8 } },
  { instanceId: 'inbox_home', widgetId: 'inbox', x: 6, y: 3, w: 6, h: 5, config: { maxItems: 10 } },
  { instanceId: 'vault_capture_home', widgetId: 'vault_capture', x: 0, y: 8, w: 4, h: 3, config: {} },
  { instanceId: 'quick_actions', widgetId: 'quick_actions', x: 4, y: 8, w: 4, h: 3, config: {} },
  { instanceId: 'google_calendar_home', widgetId: 'google_calendar', x: 8, y: 8, w: 4, h: 3, config: {} },
];

const MONITOR_HOME: WidgetGridItem[] = [
  { instanceId: 'board_health', widgetId: 'board_health', x: 0, y: 0, w: 12, h: 5, config: {} },
  { instanceId: 'my_tasks', widgetId: 'my_tasks', x: 0, y: 5, w: 6, h: 7, config: { maxItems: 12 } },
  { instanceId: 'google_calendar', widgetId: 'google_calendar', x: 6, y: 5, w: 6, h: 7, config: {} },
];

const MINIMAL_HOME: WidgetGridItem[] = [
  { instanceId: 'focus_suggestion', widgetId: 'focus_suggestion', x: 0, y: 0, w: 12, h: 4, config: {} },
  { instanceId: 'my_tasks', widgetId: 'my_tasks', x: 0, y: 4, w: 12, h: 5, config: { maxItems: 5 } },
];

export const LAYOUT_PRESETS = {
  focused: FOCUSED_HOME,
  balanced: BALANCED_HOME,
  monitor: MONITOR_HOME,
  minimal: MINIMAL_HOME,
} as const;

export type LayoutPreset = keyof typeof LAYOUT_PRESETS;

// ─── Default Tab Layouts ─────────────────────────────────────────────────────

/**
 * The factory-default layouts for every tab. These are used:
 *   1. On first load (before any user customisation).
 *   2. When the user clicks "Reset layout" in Settings.
 */
export const DEFAULT_TAB_LAYOUTS: TabLayoutMap = {
  home: BALANCED_HOME,
  my_work: [
    { instanceId: 'my_tasks', widgetId: 'my_tasks', x: 0, y: 0, w: 8, h: 10, config: { maxItems: 25 } },
    { instanceId: 'my_tasks_due', widgetId: 'my_tasks', x: 8, y: 0, w: 4, h: 5, config: { filter: 'due_today', maxItems: 5 } },
    { instanceId: 'alerts_work', widgetId: 'alerts', x: 8, y: 5, w: 4, h: 5, config: {} },
  ],
  inbox: [
    { instanceId: 'inbox', widgetId: 'inbox', x: 0, y: 0, w: 8, h: 14, config: {} },
    { instanceId: 'alerts_inbox', widgetId: 'alerts', x: 8, y: 0, w: 4, h: 7, config: {} },
    { instanceId: 'inbox_mentions', widgetId: 'inbox', x: 8, y: 7, w: 4, h: 7, config: { filterType: 'mentions', maxItems: 10 } },
  ],
  vault: [
    { instanceId: 'vault_capture', widgetId: 'vault_capture', x: 0, y: 0, w: 5, h: 7, config: {} },
    { instanceId: 'recent_notes', widgetId: 'recent_notes', x: 5, y: 0, w: 7, h: 7, config: {} },
    { instanceId: 'topics', widgetId: 'topics', x: 0, y: 7, w: 6, h: 5, config: {} },
    { instanceId: 'meetings', widgetId: 'meetings', x: 6, y: 7, w: 6, h: 5, config: {} },
  ],
  monitor: [
    { instanceId: 'board_health', widgetId: 'board_health', x: 0, y: 0, w: 6, h: 8, config: {} },
    { instanceId: 'board_pulse', widgetId: 'board_pulse', x: 6, y: 0, w: 6, h: 8, config: {} },
    { instanceId: 'alerts_monitor', widgetId: 'alerts', x: 0, y: 8, w: 12, h: 5, config: {} },
  ],
  calendar: [
    { instanceId: 'google_calendar', widgetId: 'google_calendar', x: 0, y: 0, w: 8, h: 14, config: {} },
    { instanceId: 'my_tasks_cal', widgetId: 'my_tasks', x: 8, y: 0, w: 4, h: 14, config: { filter: 'due_today', maxItems: 10 } },
  ],
  analytics: [
    { instanceId: 'analytics', widgetId: 'analytics_snapshot', x: 0, y: 0, w: 12, h: 14, config: {} },
  ],
  settings: [
    { instanceId: 'settings', widgetId: 'settings', x: 0, y: 0, w: 12, h: 14, config: {} },
  ],
  admin: [],
};

// ─── Tab Metadata ─────────────────────────────────────────────────────────────

export const TAB_META: Record<
  TabId,
  { label: string; icon: string; ariaLabel: string }
> = {
  home:      { label: 'Home',      icon: '🏠', ariaLabel: 'Home tab'      },
  my_work:   { label: 'My Work',   icon: '✅', ariaLabel: 'My Work tab'   },
  inbox:     { label: 'Inbox',     icon: '📥', ariaLabel: 'Inbox tab'     },
  vault:     { label: 'Vault',     icon: '🗄️', ariaLabel: 'Vault tab'     },
  monitor:   { label: 'Monitor',   icon: '📊', ariaLabel: 'Monitor tab'   },
  calendar:  { label: 'Calendar',  icon: '📅', ariaLabel: 'Calendar tab'  },
  analytics: { label: 'Analytics', icon: '📈', ariaLabel: 'Analytics tab' },
  settings:  { label: 'Settings',  icon: '⚙️', ariaLabel: 'Settings tab'  },
  admin:     { label: 'Admin',     icon: '🔧', ariaLabel: 'Admin tab'     },
};

// ─── Navigation Tabs ──────────────────────────────────────────────────────────

/**
 * Ordered array of tab descriptors for NavBar.
 * Derived from TAB_META so there is a single source of truth for labels/icons.
 * The Admin tab is intentionally last — it is a power-user / installer tool.
 */
const TAB_ORDER: TabId[] = [
  'home',
  'my_work',
  'inbox',
  'vault',
  'monitor',
  'calendar',
  'analytics',
  'settings',
  'admin',
];

export const APP_TABS: AppTab[] = TAB_ORDER.map((id) => ({
  id,
  ...TAB_META[id],
}));

// ─── Priority Score Weights ───────────────────────────────────────────────────

/** Weights used by the scoring engine in useIntelligence */
export const PRIORITY_WEIGHTS = {
  Critical: 100,
  High:     70,
  Medium:   40,
  Low:      10,
} as const;

/** Overdue multiplier: score += overdueMultiplier × days overdue */
export const OVERDUE_MULTIPLIER = 3;

/** Status modifiers: Blocked and Stuck items get a bonus to surface them */
export const STATUS_MODIFIERS: Record<string, number> = {
  Blocked:   20,
  Stuck:     15,
  Escalated: 25,
  'In Progress': 5,
  'To Do':   0,
  Done:      -999, // Push completed items to the bottom
};

// ─── Board Names ──────────────────────────────────────────────────────────────

/** Canonical display names for Chanelle OS boards */
export const CHANELLE_OS_BOARD_NAMES = {
  topics:        'COS — Topics',
  notes:         'COS — Notes',
  actions:       'COS — Actions',
  meetings:      'COS — Meetings',
  settings:      'COS — Settings',
  commands:      'COS — Commands',
  board_monitor: 'COS — Board Monitor',
  data_sources:  'COS — Data Sources',
  task_feed:     'COS — Task Feed',
  analytics:     'COS — Analytics',
  alerts:        'COS — Alerts',
  aggregator:    'Global Task & Mentions Aggregator',
} as const;

/**
 * Hard-coded board IDs for the aggregation infrastructure.
 * These boards already exist in Chanelle's workspace.
 */
export const FEED_BOARD_IDS = {
  task_feed:    '5092855258',
  aggregator:   '5092855497',
  data_sources: '5092854138',
} as const;

// ─── Storage Keys ─────────────────────────────────────────────────────────────

/**
 * Keys used to namespace data in the monday Storage API.
 * Every key is a function scoped to a user ID so multiple installers
 * can coexist in the same monday account without colliding.
 */
export const STORAGE_KEYS = {
  boardIds:        (userId: string) => `cos_board_ids_${userId}`,
  userSettings:    (userId: string) => `cos_user_settings_${userId}`,
  columnMappings:  (userId: string) => `cos_column_mappings_${userId}`,
  automationRules: (userId: string) => `cos_automation_rules_${userId}`,
  presetConfigs:   (userId: string) => `cos_preset_configs_${userId}`,
  installerState:  (userId: string) => `cos_installer_state_${userId}`,
  settings:        (userId: string) => `cos_settings_${userId}`,
  layout:          (userId: string, tabId: string) => `cos_layout_${userId}_${tabId}`,
} as const;

// ── Additional constants ────────────────────────────────────────────────────

/** Pixel breakpoints for react-grid-layout's Responsive component */
export const GRID_BREAKPOINTS = { xl: 1400, lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 } as const;

/** Allowed note type labels */
export const NOTE_TYPES = ['General', 'Meeting', 'Idea', 'Reference'] as const;

/** Default user settings persisted to monday Storage */
export const DEFAULT_USER_SETTINGS: UserSettings = {
  activePreset: 'default',
  darkMode: false,
  notificationsEnabled: true,
  taskLimit: 20,
  defaultPriority: 'Medium',
  theme: 'system',
  defaultTab: 'home',
  notificationPrefs: {
    showOverdueBadge: true,
    showInboxBadge: true,
  },
};

/** Priority scoring weights used by the intelligence engine */
export const DEFAULT_PRIORITY_WEIGHTS = {
  overdueMultiplier: 3,
  dueSoonDays: 3,
  priorityScores: { Critical: 100, High: 70, Medium: 40, Low: 10 },
  statusScores: { Blocked: 20, Stuck: 15, Escalated: 25, 'In Progress': 5, 'To Do': 0, Done: -999 },
};
